import Mustache from "mustache"
import sampleSize from "lodash/sampleSize"
import { recommendationTemplate } from "./templates"
import { loadUserRecentSearches } from "app/components/universal_search/search_history"
import { TOTAL_MAX_RECENT_SEARCHES } from "./recent_searches"

interface Service {
  recommended: boolean
  image: string
  [key: string]: unknown
}

const processServicesResponse = (services: Service[], totalRecomendations: number): void => {
  const recommendedServices = services.filter((service: Service) => service.recommended)
  const recommendationSample = sampleSize(recommendedServices, totalRecomendations)

  recommendationSample.forEach((service: Service) => {
    const link = document.createElement("link")
    link.rel = "preload"
    link.as = "image"
    link.href = service.image
    document.head.appendChild(link)
  })

  document.querySelectorAll(".recommended-services > ul.universal-search-helper-list").forEach(recommendedWrapper => {
    recommendedWrapper.innerHTML = ""
    recommendationSample.forEach((recommendation: Service) => {
      const rendered = Mustache.render(recommendationTemplate, recommendation)
      recommendedWrapper.insertAdjacentHTML("beforeend", rendered)
    })
  })
}

// This component is responsible to load and display recommended services
export const RecommendedServices = async (): Promise<Service[]> => {
  const totalRecomendations = 8 - (loadUserRecentSearches().slice(0, TOTAL_MAX_RECENT_SEARCHES).length || 0)
  let allServices: Service[] = []

  const cacheName = "recommended-services-cache"
  const requestUrl = "/explore/services.json"

  const fetchAndProcessServices = async (response: Response) => {
    if (response.ok) {
      allServices = await response.json()
      processServicesResponse(allServices, totalRecomendations)
    }
  }

  const fetchWithFallback = async (): Promise<void> => {
    try {
      const cache = await caches.open(cacheName)
      const cachedResponse = await cache.match(requestUrl)

      if (cachedResponse) {
        await fetchAndProcessServices(cachedResponse)
        return
      }

      const networkResponse = await fetch(requestUrl)
      if (networkResponse.ok) {
        const responseForCache = networkResponse.clone()
        await cache.put(requestUrl, responseForCache)
        await fetchAndProcessServices(networkResponse)
        return
      }

      throw new Error(`Network response not ok: ${networkResponse.status}`)
    } catch (cacheError) {
      // Cache failed, try direct fetch
      const fallbackResponse = await fetch(requestUrl)
      await fetchAndProcessServices(fallbackResponse)
    }
  }

  try {
    await fetchWithFallback()
  } catch (fallbackError) {
    window.App?.Sentry?.captureException(fallbackError, {
      tags: {
        component: "RecommendedServices",
        action: "fetch",
      },
    })
  }

  return allServices
}
